// 📄 pages/Admin/components/InterfaceFormModal.tsx
import React, { useEffect, useState } from 'react';
import { X, Save } from 'lucide-react';
import {
  InterfaceChannel,
  InterfaceChannelData,
  ObjetoNegocio,
  SolucionInterfaz,
  PlataformaInterfaz,
  DireccionInterfaz,
  OBJETOS_NEGOCIO,
  SOLUCIONES_INTERFAZ,
  PLATAFORMAS_INTERFAZ,
  DIRECCIONES_INTERFAZ,
  TECNOLOGIAS_INTERFAZ,
  MIDDLEWARES_INTERFAZ,
  plataformaInterfazMeta,
  saveInterface
} from '../../../services/catalogoInterfacesService.ts';
import { TransaccionSap, getTransacciones } from '../../../services/catalogoTransaccionesService.ts';
import { useAuth } from '../../../context/AuthContext.tsx';

interface InterfaceFormModalProps {
  item: InterfaceChannel | null; // null = alta nueva
  onClose: () => void;
  onSaved: () => void;
}

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const DATALIST_ID = 'interface-form-transacciones-datalist';

// Auto-prellenado de Transacción + Campo de búsqueda según la Tecnología
// elegida — solo si ambos campos están vacíos todavía (nunca pisa lo que
// el usuario ya haya escrito, ver handleTecnologiaChange).
const DEFAULTS_POR_TECNOLOGIA: Record<string, { transaccion: string; campoBusqueda: string }> = {
  SOAP: { transaccion: 'SRT_MONI', campoBusqueda: 'Receiver Information > Interface Name' },
  'Proxy ABAP': { transaccion: 'SRT_MONI', campoBusqueda: 'Receiver Information > Interface Name' },
  IDoc: { transaccion: 'WE02', campoBusqueda: 'Basic Type' }
};

const EMPTY_FORM: InterfaceChannelData = {
  objetoNegocio: 'contratos',
  solucion: 'general',
  plataforma: 'erp',
  transaccion: '',
  direccion: 'ingreso',
  textoBreve: '',
  nombreTecnico: '',
  campoBusqueda: '',
  palabrasClave: [],
  transaccionRefId: null,
  descripcion: '',
  tecnologia: '',
  middleware: '',
  activo: true
};

export default function InterfaceFormModal({ item, onClose, onSaved }: InterfaceFormModalProps) {
  const { user } = useAuth();
  const [form, setForm] = useState<InterfaceChannelData>(
    item
      ? {
          objetoNegocio: item.objetoNegocio || 'contratos',
          solucion: item.solucion,
          plataforma: item.plataforma,
          transaccion: item.transaccion,
          direccion: item.direccion,
          textoBreve: item.textoBreve,
          nombreTecnico: item.nombreTecnico,
          campoBusqueda: item.campoBusqueda,
          palabrasClave: item.palabrasClave,
          transaccionRefId: item.transaccionRefId,
          descripcion: item.descripcion,
          tecnologia: item.tecnologia,
          middleware: item.middleware,
          activo: item.activo
        }
      : EMPTY_FORM
  );
  // Palabras clave: mismo patrón que "tags" en KnownErrorFormModal.tsx —
  // texto separado por comas en el input, se resuelve a array al guardar.
  const [palabrasClaveInput, setPalabrasClaveInput] = useState(item?.palabrasClave?.join(', ') || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Catálogo de Transacciones ya existente, para el autocompletado del
  // campo "Transacción" — se carga una sola vez al abrir el modal.
  const [transaccionesCatalogo, setTransaccionesCatalogo] = useState<TransaccionSap[]>([]);

  useEffect(() => {
    getTransacciones()
      .then(setTransaccionesCatalogo)
      .catch((err) => console.error('Error cargando el catálogo de Transacciones para el autocompletado:', err));
  }, []);

  const handleChange = <K extends keyof InterfaceChannelData>(field: K, value: InterfaceChannelData[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // El <datalist> nativo solo devuelve el string tipeado — nunca un id —
  // así que la resolución del FK opcional (transaccionRefId) se hace acá:
  // si el texto coincide EXACTO (sin importar mayúsculas) con un código ya
  // catalogado en Transacciones, se linkea; si no, queda como texto libre
  // sin referencia (null). Se recalcula en cada tecleo para que quede
  // resuelto antes de guardar, sin necesitar un evento de "selección"
  // aparte (que el datalist no expone).
  const handleTransaccionChange = (value: string) => {
    const match = transaccionesCatalogo.find(
      (t) => t.transaccion.trim().toUpperCase() === value.trim().toUpperCase()
    );
    setForm((prev) => ({ ...prev, transaccion: value, transaccionRefId: match ? match.id : null }));
  };

  // Solo rellena lo que el usuario todavía no tocó — cada campo (Transacción
  // / Campo de búsqueda) se evalúa por separado, nunca pisa un valor ya
  // ingresado.
  const handleTecnologiaChange = (value: string) => {
    const defaults = DEFAULTS_POR_TECNOLOGIA[value];
    setForm((prev) => ({
      ...prev,
      tecnologia: value,
      transaccion: !prev.transaccion.trim() && defaults ? defaults.transaccion : prev.transaccion,
      campoBusqueda: !prev.campoBusqueda.trim() && defaults ? defaults.campoBusqueda : prev.campoBusqueda
    }));
  };

  const camposObligatoriosCompletos =
    !!form.objetoNegocio && !!form.solucion && !!form.plataforma && !!form.transaccion.trim() &&
    !!form.direccion && !!form.textoBreve.trim() && !!form.nombreTecnico.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!camposObligatoriosCompletos) {
      setError('Completa Objeto de negocio, Solución, Plataforma, Transacción, Dirección, Texto breve y Nombre técnico.');
      return;
    }
    if (!user) {
      setError('Sin sesión activa.');
      return;
    }

    setSaving(true);
    try {
      const palabrasClave = palabrasClaveInput.split(',').map((p) => p.trim()).filter(Boolean);
      await saveInterface(item?.id || generarId(), { ...form, palabrasClave }, !item, user.email || user.uid);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";
  const nombrePlataforma = plataformaInterfazMeta(form.plataforma).label;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">
            {item ? 'Editar Interfaz / Canal' : 'Nueva Interfaz / Canal'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
          )}

          {/* Fila 1: Nombre técnico — campo estrella, lo primero que se completa */}
          <div>
            <label className={labelClass}>Nombre técnico / Valor a buscar <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form.nombreTecnico}
              onChange={(e) => handleChange('nombreTecnico', e.target.value)}
              placeholder="ej. ContractRequestExportOut, ARBCIG_INVOIC, ORDERS05..."
              className={`${inputClass} font-mono`}
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Según la tecnología es un Interface Name, un Basic Type, un iFlow, etc. — es el valor que el usuario copia y pega para buscar.
            </p>
          </div>

          {/* Fila 2: Objeto de negocio / Solución */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Objeto de negocio <span className="text-red-400">*</span></label>
              <select
                className={inputClass}
                value={form.objetoNegocio}
                onChange={(e) => handleChange('objetoNegocio', e.target.value as ObjetoNegocio)}
              >
                {OBJETOS_NEGOCIO.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Solución <span className="text-red-400">*</span></label>
              <select
                className={inputClass}
                value={form.solucion}
                onChange={(e) => handleChange('solucion', e.target.value as SolucionInterfaz)}
              >
                {SOLUCIONES_INTERFAZ.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Fila 3: Plataforma / Dirección (dinámica) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Plataforma <span className="text-red-400">*</span></label>
              <select
                className={inputClass}
                value={form.plataforma}
                onChange={(e) => handleChange('plataforma', e.target.value as PlataformaInterfaz)}
              >
                {PLATAFORMAS_INTERFAZ.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Dirección (respecto a {nombrePlataforma}) <span className="text-red-400">*</span></label>
              <select
                className={inputClass}
                value={form.direccion}
                onChange={(e) => handleChange('direccion', e.target.value as DireccionInterfaz)}
              >
                {DIRECCIONES_INTERFAZ.map((d) => (
                  <option key={d.id} value={d.id}>{d.opcion(nombrePlataforma)}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">
                La dirección es relativa a la plataforma elegida arriba, no absoluta.
              </p>
            </div>
          </div>

          {/* Fila 4: Transacción / Campo de búsqueda — pegados, son la misma idea */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Transacción <span className="text-red-400">*</span></label>
              <input
                type="text"
                list={DATALIST_ID}
                value={form.transaccion}
                onChange={(e) => handleTransaccionChange(e.target.value)}
                placeholder="ej. SRT_MONI"
                className={`${inputClass} font-mono`}
              />
              <datalist id={DATALIST_ID}>
                {transaccionesCatalogo.map((t) => (
                  <option key={t.id} value={t.transaccion} />
                ))}
              </datalist>
              {form.transaccionRefId && (
                <p className="text-[11px] text-emerald-400 mt-1">Vinculada al Catálogo de Transacciones.</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Campo de búsqueda</label>
              <input
                type="text"
                value={form.campoBusqueda}
                onChange={(e) => handleChange('campoBusqueda', e.target.value)}
                placeholder="ej. Receiver Information > Interface Name"
                className={inputClass}
              />
              <p className="text-[11px] text-gray-500 mt-1">Dónde pegar el Nombre técnico dentro de la Transacción.</p>
            </div>
          </div>

          {/* Fila 5: Texto breve */}
          <div>
            <label className={labelClass}>Texto breve <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form.textoBreve}
              onChange={(e) => handleChange('textoBreve', e.target.value)}
              placeholder="ej. Interfaz de ingreso de los contratos enviados desde Ariba al ERP"
              maxLength={120}
              className={inputClass}
            />
          </div>

          {/* Fila 6: Tecnología (dispara el autofill) / Middleware */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Tecnología</label>
              <select
                className={inputClass}
                value={form.tecnologia}
                onChange={(e) => handleTecnologiaChange(e.target.value)}
              >
                <option value="">Sin especificar</option>
                {TECNOLOGIAS_INTERFAZ.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Middleware</label>
              <select
                className={inputClass}
                value={form.middleware}
                onChange={(e) => handleChange('middleware', e.target.value)}
              >
                <option value="">Sin especificar</option>
                {MIDDLEWARES_INTERFAZ.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Fila 7: Palabras clave */}
          <div>
            <label className={labelClass}>Palabras clave (separadas por coma)</label>
            <input
              type="text"
              value={palabrasClaveInput}
              onChange={(e) => setPalabrasClaveInput(e.target.value)}
              placeholder="ej. HES, hoja de entrada, service entry, SES"
              className={inputClass}
            />
            <p className="text-[11px] text-gray-500 mt-1">Sinónimos para que la búsqueda encuentre esta interfaz aunque no se escriba el nombre técnico.</p>
          </div>

          {/* Fila 8: Descripción */}
          <div>
            <label className={labelClass}>Descripción</label>
            <textarea
              value={form.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              placeholder="Detalle extendido del flujo..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !camposObligatoriosCompletos}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
